import { Link, useLocation } from "react-router-dom";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@components/ui/sidebar";
import { Collapsible, CollapsibleContent } from "@components/ui/collapsible";
import {
  BadgeEuro,
  BookOpen,
  Briefcase,
  FileText,
  FolderCheck,
  FolderDown,
  GraduationCap,
  HandCoins,
  Hexagon,
  Network,
  Send,
  UserPlus,
  Users,
  BookAudio,
  Scale,
  BadgeAlert,
  Building2,
  FileStack,
  LibraryBig,
  FolderCog,
  UserSearch,
} from "lucide-react";
import { ADMIN_URLS, isDetailRoute, isListRoute } from "src/routes";
import type { ReactElement } from "react";
import { PendingCountBadge } from "@shared-kernel/primary/shared/pending-count-badge";
import type { AdminPendingActionsVM } from "@shared-kernel/application/ports/admin/admin-repository";

// Menu labels that support badge notifications
const FUNDING_REQUESTS_TO_REVIEW_LABEL = "À traiter";
const FUNDING_REQUESTS_TO_SUBMIT_LABEL = "Traitées";
const PAYMENTS_LABEL = "Paiements";
const COLLABORATION_REQUESTS_LABEL = "Ajout d'élève";
const BATCH_ENROLLMENT_CANDIDATES_LABEL = "Cand. masterclass";
const TRAINING_INVOICES_LABEL = "Factures de formation";
const EXTERNAL_PROVIDER_REQUESTS_LABEL = "Masterclass externes";

interface AdminNavListItem {
  title: string;
  items: {
    title: string;
    url: string;
    icon: ReactElement;
  }[];
}

const adminNavList: AdminNavListItem[] = [
  {
    title: "Demandes de financement",
    items: [
      {
        title: FUNDING_REQUESTS_TO_REVIEW_LABEL,
        url: ADMIN_URLS["fundingRequest"].list,
        icon: <FolderDown />,
      },
      {
        title: FUNDING_REQUESTS_TO_SUBMIT_LABEL,
        url: ADMIN_URLS["fundingRequest"].toSubmit,
        icon: <FolderCheck />,
      },
      {
        title: "Approuvées",
        url: ADMIN_URLS["fundingRequest"].approved,
        icon: <BadgeEuro />,
      },
    ],
  },
  {
    title: "Utilisateurs",
    items: [
      {
        title: "Elèves",
        url: ADMIN_URLS["student"].list,
        icon: <GraduationCap />,
      },
      {
        title: "Professeurs",
        url: ADMIN_URLS["teacher"].list,
        icon: <Briefcase />,
      },
      {
        title: "Partenaires",
        url: ADMIN_URLS["provider"].list,
        icon: <Network />,
      },
      {
        title: "Annuaire",
        url: ADMIN_URLS["user"].list,
        icon: <Users />,
      },
      {
        title: "Invitations",
        url: ADMIN_URLS["invitation"].list,
        icon: <Send />,
      },
      {
        title: COLLABORATION_REQUESTS_LABEL,
        url: ADMIN_URLS["collaborationRequest"].list,
        icon: <UserPlus />,
      },
      {
        title: BATCH_ENROLLMENT_CANDIDATES_LABEL,
        url: ADMIN_URLS["batchEnrollmentCandidate"].list,
        icon: <UserSearch />,
      },
    ],
  },
  {
    title: "Qualité",
    items: [
      {
        title: "Cours",
        url: ADMIN_URLS["lesson"].base,
        icon: <BookOpen />,
      },
      {
        title: "Masterclass Partenaires",
        url: ADMIN_URLS["provider"].batches,
        icon: <Network />,
      },
      {
        title: "Masterclass Professeurs",
        url: ADMIN_URLS["teacher"].batches,
        icon: <Briefcase />,
      },
      {
        title: "Suivi qualité",
        url: ADMIN_URLS["trainingFollowUp"].list,
        icon: <Hexagon />,
      },
      {
        title: "Formations OPERA Off",
        url: ADMIN_URLS["internalTraining"].list,
        icon: <BookAudio />,
      },
    ],
  },
  {
    title: "Comptabilité",
    items: [
      {
        title: "Ajustement de solde",
        url: ADMIN_URLS["balanceAdjustments"].base,
        icon: <Scale />,
      },
      {
        title: PAYMENTS_LABEL,
        url: ADMIN_URLS["payment"].list,
        icon: <HandCoins />,
      },
      {
        title: TRAINING_INVOICES_LABEL,
        url: ADMIN_URLS["trainingInvoice"].list,
        icon: <FileText />,
      },
      {
        title: EXTERNAL_PROVIDER_REQUESTS_LABEL,
        url: ADMIN_URLS["externalProviderBatchRequest"].list,
        icon: <FileStack />,
      },
    ],
  },
  {
    title: "Autres",
    items: [
      {
        title: "Studios",
        url: ADMIN_URLS["studio"].list,
        icon: <Building2 />,
      },
      {
        title: "Ressources",
        url: ADMIN_URLS["resources"],
        icon: <LibraryBig />,
      },
      {
        title: "Ressources documentaires",
        url: ADMIN_URLS["documentaryResource"].list,
        icon: <FolderCog />,
      },
      {
        title: "Signalements",
        url: ADMIN_URLS["issue"].base,
        icon: <BadgeAlert />,
      },
    ],
  },
];

interface Props {
  adminPendingActions: AdminPendingActionsVM;
}

const getPendingCountForMenuItem = (itemTitle: string, adminPendingActions: AdminPendingActionsVM): number => {
  switch (itemTitle) {
    case FUNDING_REQUESTS_TO_REVIEW_LABEL:
      return adminPendingActions.fundingRequestsToReview.pendingCount;
    case FUNDING_REQUESTS_TO_SUBMIT_LABEL:
      return adminPendingActions.fundingRequestsToSubmitToFunder.pendingCount;
    case PAYMENTS_LABEL:
      return (
        adminPendingActions.teacherPayoutsToPay.pendingCount +
        adminPendingActions.standardTeacherPayoutsToPay.pendingCount +
        adminPendingActions.studentRefundsToProcess.pendingCount +
        adminPendingActions.providerBatchInvoicesToProcess.pendingCount +
        adminPendingActions.teacherBatchInvoicesToProcess.pendingCount
      );
    case COLLABORATION_REQUESTS_LABEL:
      return adminPendingActions.collaborationRequestsToReview.pendingCount;
    case BATCH_ENROLLMENT_CANDIDATES_LABEL:
      return adminPendingActions.batchEnrollmentCandidatesToReview.pendingCount;
    case TRAINING_INVOICES_LABEL:
      return adminPendingActions.trainingInvoicesToProcess.pendingCount;
    case EXTERNAL_PROVIDER_REQUESTS_LABEL:
      return (
        adminPendingActions.externalProviderRequestsToReview.pendingCount +
        adminPendingActions.externalProviderPaymentsToApprove.pendingCount
      );
    default:
      return 0;
  }
};

export const AdminSideBar = ({ adminPendingActions }: Props) => {
  const location = useLocation();
  const { isMobile, toggleSidebar } = useSidebar();

  return (
    <>
      {adminNavList.map(route => (
        <Collapsible key={route.title} title={route.title} open className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel className="group/label bg-slate-100 text-sm text-sidebar-foreground dark:bg-inherit">
              {route.title}
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {route.items.map(item => {
                    // Generic approach to determine if an item should be active
                    let isActive = false;

                    // For list routes, check if we're on a detail page
                    if (isListRoute(item.url)) {
                      if (location.pathname === item.url) {
                        // Exact match
                        isActive = true;
                      } else if (isDetailRoute(location.pathname, item.url)) {
                        // We're on a detail page for this list
                        isActive = true;
                      }
                    }
                    // For all other routes, check for exact match
                    else {
                      isActive = location.pathname === item.url;
                    }

                    const pendingCount = getPendingCountForMenuItem(item.title, adminPendingActions);

                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild isActive={isActive} tooltip={item.title} hidden={false}>
                          <Link
                            to={item.url}
                            onClick={() => {
                              if (isMobile) toggleSidebar();
                            }}
                          >
                            {item.icon} <span>{item.title}</span>
                            <PendingCountBadge count={pendingCount} className="ml-auto" />
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      ))}
    </>
  );
};
